// The league-wide weather forecast (REFOCUS §11, PLAN-04 S3).
//
// REFOCUS §11 is the reason this exists: "every player on a league day faces the
// same events, so choices decide rank rather than luck". Weather is the loudest
// event of all — a 12°F difference in air is the difference between a bird that
// lays and one that does not — so a league day whose weather followed each
// player's own city was never a comparable day, and the leaderboard it fed was
// measuring where a child happened to live rather than how she kept chickens.
//
// So the forecast is a pure function of the season and the league day, and of
// nothing else. No city, no coordinates, no clock, no network, no save. Two
// players in the same season on the same league day get the same week, on every
// device, forever — and because it is a pure function, the server's replay
// (PLAN-05) reaches the same air the phone did.
//
// The draws come from a NAMED stream keyed on the day being described, not on
// the day the forecast was read. That is what keeps the week from rewriting
// itself: today's outlook and yesterday's outlook for the same league day agree,
// so a player who plans shade for Thursday and a server replaying her log three
// days later are looking at one forecast, not two.
//
// Normal mode is the other half of the decision, and it is a decision rather
// than an omission. Normal has no league and no rank, so the comparability
// argument buys a casual player nothing; meanwhile the city they picked is the
// one weather feature they opted into, and it is what makes their sandbox feel
// like their own yard. Pinning Normal to a season forecast would take that away
// and leave the city picker in Settings as dead UI. So Normal keeps its own
// weather, `leagueForecast` returns null there, and the surfaces below fall back
// to what they showed before. The honest answer in Normal is "there is no league
// forecast", not a second opinion the sim quietly ignores.

import { hashSeed, streamFor } from '../core/rng';
import type { GameState, StormAlert, WeatherCondition, WeatherState } from '../core/types';
import { leagueContext } from './league';
import { hourOfDay } from './time';
import { stormAlertFor } from './weather';

/** Days a forecast looks ahead. A league week is seven league days. */
export const FORECAST_DAYS = 7;

/**
 * The hour of the day a tropical day peaks, a couple of hours after the sun is
 * highest. The diurnal curve is a cosine around it, because a real day's is.
 */
const PEAK_HOUR = 14.5;

/** One league day as the forecast states it. */
export interface ForecastDay {
  /** The league day this entry describes, counted from the season's day 0. */
  leagueDay: number;
  condition: WeatherCondition;
  highF: number;
  lowF: number;
  /** Daylight hours, and the local sunrise/sunset they put on the clock. */
  sunriseHour: number;
  sunsetHour: number;
  /** Day's mean relative humidity, 0-100. */
  humidity: number;
  windMph: number;
  gustMph: number;
  stormAlert: StormAlert;
}

/** The coming week, as one league sees it. */
export interface LeagueForecast {
  seasonId: string;
  /** The league day `days[0]` describes. */
  fromDay: number;
  days: ForecastDay[];
  /** The league day the save is standing on today. */
  today: number;
}

/**
 * A league day's air temperature in the shade at `hour` — the per-hour model
 * `ambientTempF` is a single snapshot of, and the one the Setup screen's hour
 * slider needs (it is what `placeholderAirTemp`'s 13-hour table was faking).
 *
 * Rounded to a tenth because this is what lands in the save: a real-time tick is
 * 1/3600 h, so the unrounded curve would write a new value to `WeatherState`
 * every frame and the autosave would churn on a number nobody can read.
 */
export function forecastAirTempF(day: ForecastDay, hour: number): number {
  const mean = (day.highF + day.lowF) / 2;
  const amplitude = (day.highF - day.lowF) / 2;
  const h = ((hour % 24) + 24) % 24;
  const temp = mean + amplitude * Math.cos(((h - PEAK_HOUR) / 24) * Math.PI * 2);
  return Math.round(temp * 10) / 10;
}

/**
 * The forecast for one league day. Derived, never read from anywhere, so it is
 * the same number however many times it is asked for and whatever the save
 * around it happens to say.
 */
function dayFor(seasonId: string, leagueDay: number): ForecastDay {
  const rng = streamFor(hashSeed(seasonId), `forecast:d${leagueDay}`);
  // Puerto Rico's own range (REFOCUS §17's launch market): warm nights, hot
  // middles, and a wet season the forecast walks in and out of rather than one
  // it is stuck in. One draw for the whole sky, so the day's character is a
  // single coherent fact rather than four dice that could fight each other.
  const roll = rng.next();
  const stormy = roll < 0.08;
  const condition: WeatherCondition = stormy
    ? 'STORM'
    : roll < 0.4
      ? 'RAIN'
      : roll < 0.68
        ? 'CLOUDY'
        : roll < 0.76
          ? 'FOG'
          : 'CLEAR';
  const lowF = rng.int(70, 79);
  const highF = lowF + rng.int(8, 16);
  // A storm day is the one that carries tropical-storm gusts and a clear
  // afternoon is not. Capping every day at 30 mph would leave `stormAlertFor`
  // nothing to read and the alert field dead — no league day could ever raise
  // the watch REFOCUS §11c asks for.
  const gustMph = stormy ? rng.int(46, 88) : rng.int(4, 26);
  const windMph = Math.min(gustMph, stormy ? rng.int(10, 34) : rng.int(2, 16));
  const daylight = 11 + rng.range(0, 2);
  const sunriseHour = 12 - daylight / 2;
  return {
    leagueDay,
    condition,
    highF,
    lowF,
    sunriseHour: Math.round(sunriseHour * 10) / 10,
    sunsetHour: Math.round((sunriseHour + daylight) * 10) / 10,
    humidity: rng.int(55, 96),
    windMph,
    gustMph,
    // The same threshold the live feed uses, so a league watch and a real
    // watch mean the same number of mph.
    stormAlert: stormAlertFor(gustMph, [stormy ? 95 : 0]),
  };
}

/**
 * One slot of memo, because `stepSimulation` asks for the league day on every
 * tick and a tick is a frame. Deriving a week is ~70 draws and seven hashes, and
 * the answer cannot change while the key holds, so this is a cache and not a
 * second source of truth. One slot is enough: only the current league day is
 * ever asked for, and the next day is a miss for exactly one frame.
 *
 * The week it hands back is shared, so treat it as read-only. Nothing mutates
 * it, and a caller that did would be corrupting every other holder of the week.
 */
let memo: { key: string; week: LeagueForecast } | null = null;

/**
 * The forecast for `[fromDay, fromDay + FORECAST_DAYS)` of a season.
 *
 * Pure in `(seasonId, fromDay)`. This is the whole contract, and it is what the
 * server replays against: nothing here reads a save, a device or a clock, so
 * there is nothing in the inputs that two players could differ on.
 */
export function forecastWeek(seasonId: string, fromDay: number): LeagueForecast {
  const start = Math.max(0, Math.floor(fromDay));
  const key = `${seasonId}|${start}`;
  if (memo?.key === key) return memo.week;
  const days: ForecastDay[] = [];
  for (let offset = 0; offset < FORECAST_DAYS; offset++) {
    days.push(dayFor(seasonId, start + offset));
  }
  const week: LeagueForecast = { seasonId, fromDay: start, days, today: start };
  memo = { key, week };
  return week;
}

/**
 * The league forecast this save is standing in, or null in Normal mode.
 *
 * Null is the Normal decision, stated once here so every surface agrees: Normal
 * has no league to be forecast for, and its own city's weather stands.
 */
export function leagueForecast(state: Readonly<GameState>): LeagueForecast | null {
  const league = leagueContext(state);
  if (!league) return null;
  return forecastWeek(league.seasonId, league.leagueDay);
}

/** The forecast day a league save is standing on, or null in Normal. */
export function leagueToday(state: Readonly<GameState>): ForecastDay | null {
  return leagueForecast(state)?.days[0] ?? null;
}

/**
 * The league day's weather as the sim's own `WeatherState`, or null in Normal.
 *
 * The whole point of the module in one value: this is the air every bird is
 * charged for, so two players in a league are solving the same problem.
 */
export function leagueWeather(state: Readonly<GameState>): WeatherState | null {
  const day = leagueToday(state);
  if (!day) return null;
  const hour = hourOfDay(state.simHour);
  return {
    condition: day.condition,
    tempF: forecastAirTempF(day, hour),
    humidity: day.humidity,
    windMph: day.windMph,
    gustMph: day.gustMph,
    isDay: hour >= day.sunriseHour && hour < day.sunsetHour,
    sunriseHour: day.sunriseHour,
    sunsetHour: day.sunsetHour,
    stormAlert: day.stormAlert,
    // Derived from the season, not fetched from anywhere.
    source: 'simulated',
    // Zero on purpose. A league day is not fetched, so "when it was read" is not
    // a fact about it — and a wall-clock stamp here would be the one input to
    // `state.weather` that a replay could not reproduce.
    fetchedAt: 0,
    // Empty on purpose. A league is not a place, and this state is uploaded
    // whole: naming one would put a location in the save that nothing measured.
    // The pill says "league day" from the locale instead.
    locationLabel: '',
  };
}

/**
 * Pins a league save's weather to the league day, and does nothing in Normal.
 *
 * Called from the top of `stepSimulation` rather than from the weather service,
 * because the air has to be right before the first bird is charged for it, and
 * because the service is asynchronous: a value that arrived over the network
 * could not be part of a replay the server re-derives from the season.
 */
export function stepLeagueWeather(state: GameState): void {
  const pinned = leagueWeather(state);
  if (!pinned) return;
  // A storm drill's alert and its ETA are the player's own state (sim/storm.ts),
  // not the season's, and the drill runs through this same code path.
  state.weather = state.storm.active
    ? {
        ...pinned,
        stormAlert: state.weather.stormAlert,
        stormEtaHour: state.weather.stormEtaHour,
      }
    : pinned;
}

/** A league day's temperature as a keeper would say it, honouring the unit setting. */
export function formatForecastTempF(tempF: number, celsius: boolean): string {
  return celsius
    ? `${Math.round(((tempF - 32) * 5) / 9)}°C`
    : `${Math.round(tempF)}°F`;
}
